import type { Promotion as TsClientPromotionDto } from '@empathybroker/tsclient-play/dist/models/promotion'
import type {
  ApiError,
  Pivot,
  PromotionAdapter,
  PromotionDTO,
} from '@empathyco/playboard-core-domain'
import { PromotionTypeEnum } from '@empathybroker/tsclient-play/dist/enums/promotion-type.enum'
import { AdapterBooleanUtils } from '@empathyco/playboard-core-domain'
import { injectable } from 'inversify'

@injectable()
export class PlatformPromotionAdapter implements PromotionAdapter<TsClientPromotionDto, ApiError> {
  decode(payload: TsClientPromotionDto): PromotionDTO {
    return {
      id: payload.id,
      schedule: this.decodeSchedule(payload.schedule),
      metadata: {
        created: payload.meta?.creationDate,
        updated: payload.meta?.updateDate,
        author: payload.meta?.user,
      },
      criteria: {
        pivots: payload.filters ? this.decodePivots(payload.filters) : [],
        queries: payload.keywords || [],
        enabled: AdapterBooleanUtils.toBoolean(payload.enabled),
        matchAll: AdapterBooleanUtils.toBoolean(payload.matchAll),
      },
      setting: {
        type: payload.type ?? PromotionTypeEnum.PROMOTED,
        title: payload.title ?? '',
        position: payload.position ?? 1,
        imageUrl: payload.imageUrl ?? '',
        config: {
          url: payload.url ?? '',
        },
      },
    }
  }

  decodeAudit(payload: TsClientPromotionDto): PromotionDTO {
    return this.decode(payload)
  }

  decodeSchedule(schedule?: TsClientPromotionDto['schedule']): PromotionDTO['schedule'] {
    return {
      activationDate: schedule?.activationDate || undefined,
      deactivationDate: schedule?.deactivationDate || undefined,
    }
  }

  decodePivots(filters: TsClientPromotionDto['filters']): PromotionDTO['criteria']['pivots'] {
    return Object.entries(filters).reduce((list, [filterKey, filterValue]) => {
      const pivot: Pivot = {
        name: filterKey,
        values: filterValue,
      }

      list.push(pivot)

      return list
    }, [] as Array<Pivot>)
  }

  decodeError(error: ApiError): ApiError {
    return error
  }

  encode(payload: PromotionDTO): TsClientPromotionDto {
    const { config, ...setting } = payload.setting
    return {
      ...setting,
      type: setting.type as TsClientPromotionDto['type'],
      url: config.url,
      id: payload.id,
      schedule: {
        activationDate: payload.schedule.activationDate ?? undefined,
        deactivationDate: payload.schedule.deactivationDate ?? undefined,
      },
      enabled: payload.criteria.enabled,
      keywords: payload.criteria.queries,
      matchAll: payload.criteria.matchAll,
      filters: this.encodePivots(payload.criteria.pivots),
    }
  }

  encodePivots(pivots: PromotionDTO['criteria']['pivots']): TsClientPromotionDto['filters'] {
    return pivots.reduce(
      (list, pivot) => {
        list[pivot.name] = pivot.values

        return list
      },
      { lang: [] } as TsClientPromotionDto['filters'],
    )
  }
}
